import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-ffdabfb6"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 2,
  class: "checkout-inner-container checkout-inner-container--card"
}
const _hoisted_2 = { class: "checkout-details" }
const _hoisted_3 = { class: "checkout-actions" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = {
  key: 0,
  class: "checkout-buy-with-card"
}
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { key: 1 }
const _hoisted_8 = {
  key: 1,
  class: "checkout-buy-with-card"
}
const _hoisted_9 = {
  key: 1,
  class: "checkout-buy-with-card"
}
const _hoisted_10 = { class: "checkout-card-input" }

import { BigNumber } from '@ethersproject/bignumber';
import { formatEther, parseUnits } from '@ethersproject/units';
import JSConfetti from 'js-confetti';
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import {
  SAVED_EMAIL_LOCAL_STORAGE_KEY, SAVED_RECIPIENT_ADDRESS_LOCAL_STORAGE_KEY
} from '@/common/constants';
import CardPaymentForm from '@/components/checkout/CardPaymentForm.vue';
import CardPaymentProcessingPage from '@/components/checkout/CardPaymentProcessingPage.vue';
import CheckoutItemsForCard from '@/components/checkout/CheckoutItemsForCard.vue';
import CheckoutSuccessPage from '@/components/checkout/CheckoutSuccessPage.vue';
import MintingQuantityError from '@/components/checkout/MintingQuantityError.vue';
import Email from '@/components/Email.vue';
import EmailInput from '@/components/EmailInput.vue';
import NumberToMintInput from '@/components/NumberToMintInput.vue';
import OptimismBanner from '@/components/OptimismBanner.vue';
import RecipientAddress from '@/components/RecipientAddress.vue';
import RecipientAddressInput from '@/components/RecipientAddressInput.vue';
import Spinner from '@/components/Spinner.vue';
import { CrossmintFiatCostBreakdown, tryGetLocalStorage, trySetLocalStorage } from '@/lib/claimUtil';
import * as telemetry from '@/lib/telemetry';
import { useClaimStore } from '@/store/claimStore';

interface BuyWithEthPageProps {
  onSuccess?: () => void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyWithCardPage',
  props: {
    onSuccess: { type: Function }
  },
  setup(__props: any) {

const props = __props as {
  onSuccess?: () => void;
};



const store = useClaimStore();

const purchaseSucceded = ref(false);

const initialEmail = ref<string | null>(null);
const initialAddress = ref<string | null>(null);

const email = ref('');

const allowListFlowState = ref<'enter-details' | 'payment-details'>('enter-details');

const isProcessingCardPayment = ref(false);
const paymentMethod = ref<'eth' | 'card'>('eth');
const itemPriceWithCard = ref<BigNumber>(BigNumber.from(0));
const manifoldFeeWithCard = ref<BigNumber>(BigNumber.from(0));
const processingFeesWithCard = ref<BigNumber>(BigNumber.from(0));
const totalCostWithCard = ref<BigNumber>(BigNumber.from(0));
const loadingPrices = ref(true);

onBeforeMount(() => {
  initialEmail.value = tryGetLocalStorage(SAVED_EMAIL_LOCAL_STORAGE_KEY);
  initialAddress.value = tryGetLocalStorage(SAVED_RECIPIENT_ADDRESS_LOCAL_STORAGE_KEY);
});

onMounted(() => {
  // HACK: Need this since at this stage we don't have a wallet to check if they are on the allow list
  // which makes Crossmint's simulation of the tx fail and therefore we can't get the price
  onCardPaymentCostChanged({
    price: parseUnits(`${(store.totalCostInUsd).toFixed(2)}`, 2),
    gasFees: parseUnits(`${(store.manifoldFeeInUsd).toFixed(2)}`, 2),
    // HACK: We add 3% to the total cost to account for the ~3% fee that Crossmint charges
    totalPrice: parseUnits(`${(store.totalCostInUsd + store.totalCostInUsd * 0.03).toFixed(2)}`, 2)
  });
});

const onEmailChanged = (newEmail: string) => {
  email.value = newEmail;
};

const onRecipientAddressChanged = async (newAddress: string) => {
  store.mintForWallet = newAddress;

  if (newAddress) {
    await store.resetMerkleData();
  }
};

const canGoToPaymentDetails = computed(() => {
  return !!email.value && !!store.mintForWallet && store.canMint && !store.isResetingMerkleData;
});

const onCardPaymentStarted = () => {
  telemetry.paymentAttempt('card', store.tokensToMint, store.mintForWallet ? 'different-wallet' : 'custodial-wallet');
  isProcessingCardPayment.value = true;
};

const onCardPaymentSuccess = (paymentMethodType: string) => {
  new JSConfetti().addConfetti({
    emojis: ['🎉'],
    confettiRadius: 90,
    confettiNumber: 200
  });

  paymentMethod.value = 'card';
  isProcessingCardPayment.value = false;
  purchaseSucceded.value = true;

  telemetry.paymentSuccess(
    'card',
    store.tokensToMint,
    store.mintForWallet ? 'different-wallet' : 'custodial-wallet',
    paymentMethodType
  );

  // Save their wallet and email address if successful mint!
  trySetLocalStorage(SAVED_EMAIL_LOCAL_STORAGE_KEY, email.value);

  if (store.mintForWallet) {
    trySetLocalStorage(SAVED_RECIPIENT_ADDRESS_LOCAL_STORAGE_KEY, store.mintForWallet);
  }

  if (props.onSuccess) {
    props.onSuccess();
  }
};

const onCardPaymentError = () => {
  isProcessingCardPayment.value = false;
  telemetry.paymentError('card', store.tokensToMint, store.mintForWallet ? 'different-wallet' : 'custodial-wallet');
};

const onCardPaymentCostChanged = (costBreakdown: CrossmintFiatCostBreakdown) => {
  const totalManifoldFees = BigNumber.from(((store.manifoldFeeInUsd || store.tokensToMint) * 100).toFixed(0));
  manifoldFeeWithCard.value = totalManifoldFees;
  totalCostWithCard.value = costBreakdown.totalPrice;
  if (store.isFreeClaim) {
    itemPriceWithCard.value = BigNumber.from(0);
    processingFeesWithCard.value = costBreakdown.totalPrice.sub(totalManifoldFees);
  } else {
    itemPriceWithCard.value = costBreakdown.price.sub(totalManifoldFees);
    processingFeesWithCard.value = costBreakdown.totalPrice.sub(totalManifoldFees).sub(itemPriceWithCard.value);
  }
  loadingPrices.value = false;
};

const onCardPaymentRecipientWalletChanged = async (wallet: string) => {
  store.mintForWallet = wallet;
  await store.resetMerkleData();
};

return (_ctx: any,_cache: any) => {
  return (purchaseSucceded.value)
    ? (_openBlock(), _createBlock(CheckoutSuccessPage, {
        key: 0,
        "payment-method": 'card',
        email: email.value,
        "recipient-address": _unref(store).mintForWallet
      }, null, 8, ["email", "recipient-address"]))
    : (isProcessingCardPayment.value)
      ? (_openBlock(), _createBlock(CardPaymentProcessingPage, { key: 1 }))
      : (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(NumberToMintInput),
            _createVNode(CheckoutItemsForCard, {
              "number-to-mint": _unref(store).tokensToMint,
              "item-price": itemPriceWithCard.value,
              "manifold-fee": manifoldFeeWithCard.value,
              "processing-fees": processingFeesWithCard.value,
              "total-cost": totalCostWithCard.value,
              "loading-prices": loadingPrices.value
            }, null, 8, ["number-to-mint", "item-price", "manifold-fee", "processing-fees", "total-cost", "loading-prices"]),
            _createVNode(OptimismBanner, { class: "checkout-network" })
          ]),
          _createElementVNode("div", _hoisted_3, [
            (_unref(store).hasAllowlist)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  (allowListFlowState.value === 'enter-details')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _createVNode(EmailInput, {
                          "initial-email": initialEmail.value,
                          onOnEmailChanged: onEmailChanged
                        }, null, 8, ["initial-email"]),
                        _createVNode(RecipientAddressInput, {
                          "initial-address": initialAddress.value,
                          onOnAddressChanged: onRecipientAddressChanged,
                          required: ""
                        }, null, 8, ["initial-address"]),
                        _createVNode(MintingQuantityError),
                        _createElementVNode("button", {
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (allowListFlowState.value = 'payment-details')),
                          class: "campaign-buy-button",
                          disabled: !_unref(canGoToPaymentDetails)
                        }, [
                          (_unref(store).isResetingMerkleData)
                            ? (_openBlock(), _createBlock(Spinner, {
                                key: 0,
                                dark: ""
                              }))
                            : (_openBlock(), _createElementBlock("span", _hoisted_7, "Next"))
                        ], 8, _hoisted_6)
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createVNode(Email, { email: email.value }, null, 8, ["email"]),
                        _createVNode(RecipientAddress, {
                          address: _unref(store).mintForWallet || null
                        }, null, 8, ["address"]),
                        _createVNode(CardPaymentForm, {
                          network: _unref(store).networkId || 1,
                          email: email.value,
                          type: _unref(store).claimType,
                          "mint-count": _unref(store).tokensToMint,
                          "total-price": _unref(formatEther)(_unref(store).totalCost),
                          "creator-contract-address": _unref(store).creatorContractAddress,
                          "claims-contract-address": _unref(store).overrideMintExtensionAddress ? _unref(store).overrideMintExtensionAddress : _unref(store).extensionAddress,
                          "crossmint-client-id": _unref(store).crossmintClientId,
                          "claim-index": _unref(store).claimIndex,
                          "merkle-proofs": _unref(store).claimableMerkleProofs || [],
                          "mint-indices": _unref(store).claimableMintIndices || [],
                          "mint-for": _unref(store).mintForWallet || null,
                          "on-payment-started": onCardPaymentStarted,
                          "on-success": onCardPaymentSuccess,
                          "on-error": onCardPaymentError,
                          "on-cost-changed": onCardPaymentCostChanged,
                          "on-recipient-wallet-changed": () => {}
                        }, null, 8, ["network", "email", "type", "mint-count", "total-price", "creator-contract-address", "claims-contract-address", "crossmint-client-id", "claim-index", "merkle-proofs", "mint-indices", "mint-for", "on-recipient-wallet-changed"])
                      ]))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(EmailInput, {
                    "initial-email": initialEmail.value,
                    onOnEmailChanged: onEmailChanged
                  }, null, 8, ["initial-email"]),
                  _createVNode(RecipientAddressInput, {
                    "initial-address": initialAddress.value,
                    onOnAddressChanged: onRecipientAddressChanged
                  }, null, 8, ["initial-address"]),
                  _createVNode(MintingQuantityError),
                  _createElementVNode("div", _hoisted_10, [
                    _createVNode(CardPaymentForm, {
                      network: _unref(store).networkId || 1,
                      email: email.value,
                      type: _unref(store).claimType,
                      "mint-count": _unref(store).tokensToMint,
                      "total-price": _unref(formatEther)(_unref(store).totalCost),
                      "creator-contract-address": _unref(store).creatorContractAddress,
                      "claims-contract-address": _unref(store).overrideMintExtensionAddress ? _unref(store).overrideMintExtensionAddress : _unref(store).extensionAddress,
                      "crossmint-client-id": _unref(store).crossmintClientId,
                      "claim-index": _unref(store).claimIndex,
                      "merkle-proofs": _unref(store).claimableMerkleProofs || [],
                      "mint-indices": _unref(store).claimableMintIndices || [],
                      "mint-for": _unref(store).mintForWallet || null,
                      "on-payment-started": onCardPaymentStarted,
                      "on-success": onCardPaymentSuccess,
                      "on-error": onCardPaymentError,
                      "on-cost-changed": onCardPaymentCostChanged,
                      "on-recipient-wallet-changed": onCardPaymentRecipientWalletChanged
                    }, null, 8, ["network", "email", "type", "mint-count", "total-price", "creator-contract-address", "claims-contract-address", "crossmint-client-id", "claim-index", "merkle-proofs", "mint-indices", "mint-for"])
                  ])
                ]))
          ])
        ]))
}
}

})